import { z } from 'zod'
import { Environment, IsoDateTime, Role } from './common'

export const WorkspaceKind = z.enum(['lender-hub', 'bre', 'campaign', 'uploader', 'demo'])
export type WorkspaceKind = z.infer<typeof WorkspaceKind>

/** The portal: one install of the_Kit. */
export const Portal = z.object({
  id: z.string(),
  name: z.string(),
  environment: Environment,
  version: z.string().describe('the_Kit version'),
})
export type Portal = z.infer<typeof Portal>

/** A workspace: its own roles, node packs, run queue, limits and Postgres schema. */
export const Workspace = z.object({
  id: z.string(),
  slug: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  kind: WorkspaceKind,
  environment: Environment,
  schemaName: z.string().describe('Postgres schema of the workspace tables, e.g. lender_hub'),
  nodePacks: z.array(z.string()).describe('ids of the installed node packs'),
  theme: z.object({
    primary: z.string().describe('hex colour of the primary (teal by default)'),
    accent: z.string().describe('hex colour of the accent (gold by default)'),
    logoText: z.string().max(3).describe('1–3 letters shown as the logo mark'),
  }),
  limits: z.object({
    runsPerMinute: z.number().int().min(1),
    maxConcurrentRuns: z.number().int().min(1),
    runRecordDays: z.number().int().min(1).describe('run records kept this many days (P6: 90)'),
  }),
  createdAt: IsoDateTime,
})
export type Workspace = z.infer<typeof Workspace>

export const WorkspacePatch = Workspace.pick({
  name: true,
  theme: true,
  limits: true,
  environment: true,
}).partial()
export type WorkspacePatch = z.infer<typeof WorkspacePatch>

/** A person. Roles are per workspace; a user missing from a workspace has no access to it. */
export const User = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  roles: z.record(z.string(), Role).describe('workspace id → role'),
  createdAt: IsoDateTime,
  lastSeenAt: IsoDateTime.optional(),
})
export type User = z.infer<typeof User>

export const UserInput = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  role: Role,
})
export type UserInput = z.infer<typeof UserInput>
